import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient, useAuth, API } from "../App";
import useScrollRestoration from "../hooks/useScrollRestoration";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import {
  Plus, FileText, Send, Download, CreditCard, Trash2, Edit,
  CheckCircle2, AlertCircle, Wallet, Receipt, Search, Filter,
  Calculator, CalendarRange, Link2,
} from "lucide-react";

const STATUS_META = {
  draft:     { label: "Draft",     cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  sent:      { label: "Sent",      cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  part_paid: { label: "Part Paid", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  paid:      { label: "Paid",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  overdue:   { label: "Overdue",   cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-700/40 text-zinc-400 border-zinc-700" },
};

const blankLine = () => ({ description: "", quantity: 1, unit_price: 0, vat_rate: 0 });

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (d) => {
  const x = new Date();
  x.setDate(x.getDate() + d);
  return x.toISOString().slice(0, 10);
};

const fmtMoney = (amount, currency = "GBP") => {
  const sym = currency === "GBP" ? "£" : `${currency} `;
  const n = Number(amount || 0);
  return `${sym}${n.toFixed(2)}`;
};

const fmtDateUK = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
};

const Invoices = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [perm, setPerm] = useState({ can_manage_invoices: false, is_admin: false, loaded: false });
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  // iter-86: preserve scroll position on invoice detail navigation
  useScrollRestoration({ ready: !loading });

  // Create / edit
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blankForm = () => ({
    recipient_name: "",
    recipient_email: "",
    billing_address: "",
    issue_date: today(),
    due_date: plusDays(30),
    description: "",
    line_items: [blankLine()],
    discount: 0,
    notes: "",
    currency: "GBP",
    event_id: null,
    purchase_order_number: "",
  });
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  // Detail
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Payment
  const [paymentOpen, setPaymentOpen] = useState(false);
  const blankPayment = () => ({ amount: 0, paid_on: today(), method: "bank_transfer", reference: "", notes: "" });
  const [payment, setPayment] = useState(blankPayment());
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get("/invoicing/permissions");
        setPerm({ ...r.data, loaded: true });
      } catch {
        setPerm({ can_manage_invoices: false, is_admin: false, loaded: true });
      }
    })();
  }, [user?.id, user?.band_id]);

  useEffect(() => {
    if (!perm.loaded) return;
    if (!perm.can_manage_invoices) {
      setLoading(false);
      return;
    }
    fetchAll();
  }, [perm.loaded, perm.can_manage_invoices, user?.band_id]);

  // URL-param driven prefill — arrive from EventDetail "Invoice this booker"
  // e.g. /invoices?open=new&event_id=XYZ&desc=Concert%20fee%20—%20Spring%20Gala&line_desc=Performance%20fee&line_unit=500
  useEffect(() => {
    if (!perm.loaded || !perm.can_manage_invoices) return;
    const params = new URLSearchParams(location.search);
    if (params.get("open") !== "new") return;
    const f = {
      ...blankForm(),
      event_id: params.get("event_id") || null,
      description: params.get("desc") || "",
      recipient_name: params.get("recipient") || "",
      recipient_email: params.get("email") || "",
      line_items: [
        {
          ...blankLine(),
          description: params.get("line_desc") || params.get("desc") || "",
          quantity: 1,
          unit_price: Number(params.get("line_unit") || 0),
          vat_rate: Number(params.get("vat_rate") || 0),
        },
      ],
    };
    setEditing(null);
    setForm(f);
    setFormOpen(true);
    // Clean the URL so this doesn't re-trigger on re-render
    navigate("/invoices", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perm.loaded, perm.can_manage_invoices]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([
        apiClient.get("/invoices/dashboard"),
        apiClient.get("/invoices"),
      ]);
      setDashboard(d.data);
      setInvoices(l.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let rows = invoices;
    if (filterStatus !== "all") rows = rows.filter((r) => r.status === filterStatus);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.number || "").toLowerCase().includes(q) ||
          (r.recipient_name || "").toLowerCase().includes(q) ||
          (r.recipient_email || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [invoices, filterStatus, search]);

  // ---------- Form helpers ----------
  const openCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setFormOpen(true);
  };
  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      recipient_name: inv.recipient_name || "",
      recipient_email: inv.recipient_email || "",
      billing_address: inv.billing_address || "",
      issue_date: inv.issue_date || today(),
      due_date: inv.due_date || plusDays(30),
      description: inv.description || "",
      line_items: (inv.line_items || []).length ? inv.line_items.map((li) => ({ ...li })) : [blankLine()],
      discount: inv.discount || 0,
      notes: inv.notes || "",
      currency: inv.currency || "GBP",
      event_id: inv.event_id || null,
      purchase_order_number: inv.purchase_order_number || "",
    });
    setFormOpen(true);
  };

  const updateLine = (idx, key, val) => {
    setForm((f) => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [key]: val };
      return { ...f, line_items: items };
    });
  };
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, blankLine()] }));
  const removeLine = (idx) =>
    setForm((f) => ({
      ...f,
      line_items: f.line_items.filter((_, i) => i !== idx).length ? f.line_items.filter((_, i) => i !== idx) : [blankLine()],
    }));

  const formTotals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    for (const li of form.line_items) {
      const sub = Number(li.quantity || 0) * Number(li.unit_price || 0);
      subtotal += sub;
      vat += sub * Number(li.vat_rate || 0) / 100;
    }
    const discount = Number(form.discount || 0);
    return {
      subtotal: +subtotal.toFixed(2),
      vat: +vat.toFixed(2),
      discount: +discount.toFixed(2),
      total: +(subtotal + vat - discount).toFixed(2),
    };
  }, [form.line_items, form.discount]);

  const submitForm = async (e) => {
    e?.preventDefault?.();
    if (!form.recipient_name.trim()) {
      toast.error("Recipient name is required");
      return;
    }
    if (!form.line_items.some((li) => li.description?.trim())) {
      toast.error("Add at least one line item with a description");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        line_items: form.line_items.map((li) => ({
          description: li.description || "",
          quantity: Number(li.quantity || 0),
          unit_price: Number(li.unit_price || 0),
          vat_rate: Number(li.vat_rate || 0),
        })),
        discount: Number(form.discount || 0),
        event_id: form.event_id || null,
      };
      if (!payload.recipient_email) delete payload.recipient_email;
      if (editing) {
        await apiClient.put(`/invoices/${editing.id}`, payload);
        toast.success(`Invoice ${editing.number} updated`);
      } else {
        const r = await apiClient.post("/invoices", payload);
        toast.success(`Invoice ${r.data.number} created`);
      }
      setFormOpen(false);
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Actions ----------
  const openDetail = async (inv) => {
    try {
      const r = await apiClient.get(`/invoices/${inv.id}`);
      setDetail(r.data);
      setDetailOpen(true);
    } catch (e) {
      toast.error("Failed to load invoice");
    }
  };

  const sendInvoice = async (inv) => {
    if (!inv.recipient_email) {
      toast.error("Add a recipient email first");
      return;
    }
    if (!confirm(`Send invoice ${inv.number} to ${inv.recipient_email}?`)) return;
    try {
      await apiClient.post(`/invoices/${inv.id}/send`);
      toast.success(`Invoice ${inv.number} sent`);
      fetchAll();
      if (detail?.id === inv.id) openDetail(inv);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  const downloadPdf = async (inv) => {
    const token = localStorage.getItem("token");
    const url = `${API}/invoices/${inv.id}/pdf`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${inv.number}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const downloadReceipt = async (paymentId, receiptNumber) => {
    const token = localStorage.getItem("token");
    const url = `${API}/invoice-payments/${paymentId}/receipt.pdf`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${receiptNumber || "receipt"}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Failed to download receipt");
    }
  };

  const cancelInvoice = async (inv) => {
    if (!confirm(`Cancel invoice ${inv.number}? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/invoices/${inv.id}`);
      toast.success(`Invoice ${inv.number} cancelled`);
      setDetailOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    }
  };

  const openPayment = (inv) => {
    setPayment({ ...blankPayment(), amount: Number(inv.balance || inv.total || 0).toFixed(2) });
    setPaymentOpen(true);
  };

  const submitPayment = async (e) => {
    e?.preventDefault?.();
    if (!detail) return;
    if (Number(payment.amount) <= 0) {
      toast.error("Amount must be greater than zero");
      return;
    }
    setRecording(true);
    try {
      const payload = { ...payment, amount: Number(payment.amount) };
      if (!payload.reference) delete payload.reference;
      if (!payload.notes) delete payload.notes;
      const r = await apiClient.post(`/invoices/${detail.id}/payments`, payload);
      toast.success(`Payment ${r.data.payment.receipt_number} recorded`);
      setPaymentOpen(false);
      // Refresh both list and detail
      fetchAll();
      const upd = await apiClient.get(`/invoices/${detail.id}`);
      setDetail(upd.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally {
      setRecording(false);
    }
  };

  // ---------- Renders ----------
  if (!perm.loaded || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="invoices-loading">
        <div className="animate-pulse text-muted-foreground">Loading invoices...</div>
      </div>
    );
  }

  if (!perm.can_manage_invoices) {
    return (
      <div className="max-w-2xl mx-auto mt-12" data-testid="invoices-no-access">
        <Card className="glass-card">
          <CardContent className="p-8 text-center space-y-3">
            <AlertCircle className="w-10 h-10 text-amber-400 mx-auto" />
            <h2 className="font-heading text-xl">Treasurer access required</h2>
            <p className="text-muted-foreground text-sm">
              Only band admins or members with the <strong>Treasurer</strong> flag can view and
              manage invoices. Ask an admin to enable this for you in <em>Band Settings → Manage
              Member Roles</em>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight">Invoices</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Bill recipients, track payments, and download PDF invoices &amp; receipts.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            asChild
            data-testid="invoices-export-csv-btn"
          >
            <a
              href={`${API}/invoices/export.csv`}
              onClick={async (e) => {
                e.preventDefault();
                const token = localStorage.getItem("token");
                const r = await fetch(`${API}/invoices/export.csv`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const blob = await r.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "invoices.csv";
                a.click();
                URL.revokeObjectURL(a.href);
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </a>
          </Button>
          <Button onClick={openCreate} data-testid="invoices-new-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Total Invoiced"
          value={fmtMoney(dashboard?.total_invoiced)}
          icon={<FileText className="w-4 h-4 text-blue-400" />}
          testid="kpi-total-invoiced"
        />
        <KpiCard
          label="Total Paid"
          value={fmtMoney(dashboard?.total_paid)}
          icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />}
          testid="kpi-total-paid"
        />
        <KpiCard
          label="Outstanding"
          value={fmtMoney(dashboard?.outstanding)}
          icon={<Wallet className="w-4 h-4 text-amber-400" />}
          testid="kpi-outstanding"
        />
        <KpiCard
          label="Overdue"
          value={`${dashboard?.overdue_count || 0}`}
          icon={<AlertCircle className="w-4 h-4 text-red-400" />}
          testid="kpi-overdue"
        />
      </div>

      {/* Filters + table */}
      <Card className="glass-card">
        <CardHeader className="border-b border-white/5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <CardTitle className="font-heading flex items-center gap-2">
              <Receipt className="w-5 h-5 text-primary" />
              All invoices
            </CardTitle>
            <div className="flex-1" />
            <div className="flex gap-2 items-center">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
                <Input
                  placeholder="Search number, recipient..."
                  className="pl-9 w-[220px]"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="invoices-search-input"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]" data-testid="invoices-status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="part_paid">Part Paid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No invoices yet. Click <strong>New Invoice</strong> to create one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table data-testid="invoices-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((inv) => {
                  const meta = STATUS_META[inv.status] || STATUS_META.draft;
                  return (
                    <TableRow
                      key={inv.id}
                      className="cursor-pointer hover:bg-white/[0.02]"
                      onClick={() => openDetail(inv)}
                      data-testid={`invoice-row-${inv.number}`}
                    >
                      <TableCell className="font-mono text-sm">{inv.number}</TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{inv.recipient_name}</div>
                        {inv.recipient_email && (
                          <div className="text-xs text-muted-foreground">{inv.recipient_email}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDateUK(inv.issue_date)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDateUK(inv.due_date)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(inv.total, inv.currency)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {fmtMoney(inv.balance, inv.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${meta.cls} text-xs`}>
                          {meta.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {inv.status !== "paid" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = `${window.location.origin}/invoices/${inv.id}/pay`;
                                navigator.clipboard.writeText(link).then(
                                  () => toast.success("Pay link copied"),
                                  () => toast.error("Could not copy link")
                                );
                              }}
                              data-testid={`invoice-copy-pay-link-${inv.number}`}
                              title="Copy Pay Now link"
                            >
                              <Link2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetail(inv);
                            }}
                            data-testid={`invoice-view-${inv.number}`}
                          >
                            View
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent payments */}
      {dashboard?.recent_payments?.length > 0 && (
        <Card className="glass-card">
          <CardHeader className="border-b border-white/5">
            <CardTitle className="font-heading flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-400" />
              Recent payments
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Receipt</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.recent_payments.map((p) => (
                  <TableRow key={p.id} data-testid={`recent-payment-${p.receipt_number}`}>
                    <TableCell className="font-mono text-xs">{p.receipt_number}</TableCell>
                    <TableCell className="font-mono text-xs">{p.invoice_number}</TableCell>
                    <TableCell className="text-sm">{fmtDateUK(p.paid_on)}</TableCell>
                    <TableCell className="text-sm capitalize">{(p.method || "").replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-right">{fmtMoney(p.amount)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadReceipt(p.id, p.receipt_number)}
                        data-testid={`receipt-download-${p.receipt_number}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---------------- Tax / VAT summary ---------------- */}
      <TaxSummaryCard />

      {/* ---------------- Create / Edit dialog ---------------- */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit invoice ${editing.number}` : "New invoice"}</DialogTitle>
            {form.event_id && (
              <p className="text-xs text-muted-foreground" data-testid="invoice-form-event-link">
                <Link2 className="w-3 h-3 inline mr-1" />
                Linked to an event — totals will feed into the event's finance view.
              </p>
            )}
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="recipient_name">Recipient name *</Label>
                <Input
                  id="recipient_name"
                  required
                  value={form.recipient_name}
                  onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                  data-testid="invoice-recipient-name"
                />
              </div>
              <div>
                <Label htmlFor="recipient_email">Recipient email</Label>
                <Input
                  id="recipient_email"
                  type="email"
                  value={form.recipient_email}
                  onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                  data-testid="invoice-recipient-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="billing_address">Billing address</Label>
              <Textarea
                id="billing_address"
                rows={2}
                value={form.billing_address}
                onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
                data-testid="invoice-billing-address"
              />
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="issue_date">Issue date *</Label>
                <Input
                  id="issue_date"
                  type="date"
                  required
                  value={form.issue_date}
                  onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                  data-testid="invoice-issue-date"
                />
              </div>
              <div>
                <Label htmlFor="due_date">Due date *</Label>
                <Input
                  id="due_date"
                  type="date"
                  required
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  data-testid="invoice-due-date"
                />
              </div>
              <div>
                <Label htmlFor="currency">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger id="currency" data-testid="invoice-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GBP">GBP £</SelectItem>
                    <SelectItem value="EUR">EUR €</SelectItem>
                    <SelectItem value="USD">USD $</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Short description</Label>
              <Input
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. Performance fee – Christmas concert"
                data-testid="invoice-description"
              />
            </div>

            <div>
              <Label htmlFor="purchase_order_number">Purchase order number (optional)</Label>
              <Input
                id="purchase_order_number"
                value={form.purchase_order_number}
                onChange={(e) => setForm({ ...form, purchase_order_number: e.target.value })}
                placeholder="e.g. PO-2026-0184"
                maxLength={60}
                data-testid="invoice-po-number"
              />
              <p className="text-xs text-muted-foreground mt-1">
                If your customer requires a PO number, enter it here — it will appear on the invoice PDF.
              </p>
            </div>

            {/* Line items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Line items</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addLine} data-testid="invoice-add-line-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add line
                </Button>
              </div>
              <div className="space-y-2">
                {form.line_items.map((li, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-end p-3 bg-white/[0.02] rounded-md border border-white/[0.04]"
                    data-testid={`invoice-line-${idx}`}
                  >
                    <div className="col-span-12 sm:col-span-5">
                      <Label className="text-[10px] uppercase tracking-wider">Description</Label>
                      <Input
                        value={li.description}
                        onChange={(e) => updateLine(idx, "description", e.target.value)}
                        data-testid={`invoice-line-desc-${idx}`}
                      />
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <Label className="text-[10px] uppercase tracking-wider">Qty</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={li.quantity}
                        onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                        data-testid={`invoice-line-qty-${idx}`}
                      />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <Label className="text-[10px] uppercase tracking-wider">Unit</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={li.unit_price}
                        onChange={(e) => updateLine(idx, "unit_price", e.target.value)}
                        data-testid={`invoice-line-unit-${idx}`}
                      />
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <Label className="text-[10px] uppercase tracking-wider">VAT %</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={li.vat_rate}
                        onChange={(e) => updateLine(idx, "vat_rate", e.target.value)}
                        data-testid={`invoice-line-vat-${idx}`}
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1 flex justify-end">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeLine(idx)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`invoice-line-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="discount">Discount (flat)</Label>
                <Input
                  id="discount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.discount}
                  onChange={(e) => setForm({ ...form, discount: e.target.value })}
                  data-testid="invoice-discount"
                />
              </div>
              <div className="text-right text-sm bg-white/[0.02] rounded-md border border-white/[0.04] p-3 space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(formTotals.subtotal, form.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">VAT</span><span>{fmtMoney(formTotals.vat, form.currency)}</span></div>
                {formTotals.discount > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>-{fmtMoney(formTotals.discount, form.currency)}</span></div>
                )}
                <div className="flex justify-between border-t border-white/10 pt-1 font-bold">
                  <span>Total</span>
                  <span data-testid="invoice-form-total">{fmtMoney(formTotals.total, form.currency)}</span>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes / payment instructions</Label>
              <Textarea
                id="notes"
                rows={3}
                placeholder="Bank account details, payment terms, etc."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="invoice-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="invoice-save-btn">
                {saving ? "Saving..." : editing ? "Update invoice" : "Create invoice"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Detail dialog ---------------- */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-dialog">
          {detail && (() => {
            const meta = STATUS_META[detail.status] || STATUS_META.draft;
            const editable = !["paid", "cancelled"].includes(detail.status);
            const canPay = !["cancelled", "paid"].includes(detail.status) && (detail.balance || 0) > 0;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <span className="font-mono">{detail.number}</span>
                    <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                    {detail.event_id && (
                      <Badge
                        variant="outline"
                        className="bg-violet-500/10 text-violet-300 border-violet-500/30 text-xs cursor-pointer"
                        onClick={() => navigate(`/events/${detail.event_id}`)}
                        data-testid="invoice-detail-event-link"
                      >
                        <Link2 className="w-3 h-3 mr-1" />
                        Linked to event
                      </Badge>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Bill to</p>
                      <p className="font-medium">{detail.recipient_name}</p>
                      {detail.recipient_email && <p className="text-muted-foreground">{detail.recipient_email}</p>}
                      {detail.billing_address && (
                        <p className="text-muted-foreground whitespace-pre-line">{detail.billing_address}</p>
                      )}
                    </div>
                    <div className="text-sm space-y-0.5">
                      <p><span className="text-muted-foreground">Issue:</span> {fmtDateUK(detail.issue_date)}</p>
                      <p><span className="text-muted-foreground">Due:</span> {fmtDateUK(detail.due_date)}</p>
                      {detail.description && <p><span className="text-muted-foreground">For:</span> {detail.description}</p>}
                      {detail.purchase_order_number && (
                        <p data-testid="invoice-detail-po"><span className="text-muted-foreground">PO #:</span> {detail.purchase_order_number}</p>
                      )}
                    </div>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit</TableHead>
                        <TableHead className="text-right">VAT %</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(detail.line_items || []).map((li, i) => {
                        const sub = Number(li.quantity || 0) * Number(li.unit_price || 0);
                        const total = sub + sub * Number(li.vat_rate || 0) / 100;
                        return (
                          <TableRow key={i}>
                            <TableCell>{li.description}</TableCell>
                            <TableCell className="text-right">{Number(li.quantity)}</TableCell>
                            <TableCell className="text-right">{fmtMoney(li.unit_price, detail.currency)}</TableCell>
                            <TableCell className="text-right">{Number(li.vat_rate || 0)}%</TableCell>
                            <TableCell className="text-right">{fmtMoney(total, detail.currency)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  <div className="flex justify-end">
                    <div className="w-64 space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(detail.subtotal, detail.currency)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">VAT</span><span>{fmtMoney(detail.vat, detail.currency)}</span></div>
                      {(detail.discount || 0) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>-{fmtMoney(detail.discount, detail.currency)}</span></div>}
                      <div className="flex justify-between border-t border-white/10 pt-1 font-bold">
                        <span>Total</span>
                        <span>{fmtMoney(detail.total, detail.currency)}</span>
                      </div>
                      <div className="flex justify-between text-emerald-400">
                        <span>Paid</span>
                        <span data-testid="invoice-detail-paid">{fmtMoney(detail.amount_paid || 0, detail.currency)}</span>
                      </div>
                      <div className="flex justify-between font-bold">
                        <span>Balance</span>
                        <span data-testid="invoice-detail-balance">{fmtMoney(detail.balance, detail.currency)}</span>
                      </div>
                    </div>
                  </div>

                  {detail.notes && (
                    <div className="p-3 bg-white/[0.02] rounded-md border border-white/[0.04] text-sm">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Notes</p>
                      <p className="whitespace-pre-line">{detail.notes}</p>
                    </div>
                  )}

                  {detail.payments?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Payments</p>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Receipt</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Method</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {detail.payments.map((p) => (
                            <TableRow key={p.id} data-testid={`invoice-payment-${p.receipt_number}`}>
                              <TableCell className="font-mono text-xs">{p.receipt_number}</TableCell>
                              <TableCell className="text-sm">{fmtDateUK(p.paid_on)}</TableCell>
                              <TableCell className="text-sm capitalize">{(p.method || "").replace(/_/g, " ")}</TableCell>
                              <TableCell className="text-sm">{p.reference || "—"}</TableCell>
                              <TableCell className="text-right">{fmtMoney(p.amount, detail.currency)}</TableCell>
                              <TableCell className="text-right">
                                <Button size="sm" variant="ghost" onClick={() => downloadReceipt(p.id, p.receipt_number)}>
                                  <Download className="w-3.5 h-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                <DialogFooter className="flex-wrap gap-2">
                  <Button variant="outline" onClick={() => downloadPdf(detail)} data-testid="invoice-detail-download-pdf">
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                  {detail.recipient_email && detail.status !== "cancelled" && (
                    <Button variant="outline" onClick={() => sendInvoice(detail)} data-testid="invoice-detail-send">
                      <Send className="w-4 h-4 mr-2" />
                      Send by email
                    </Button>
                  )}
                  {editable && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setDetailOpen(false);
                        openEdit(detail);
                      }}
                      data-testid="invoice-detail-edit"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  {canPay && (
                    <Button onClick={() => openPayment(detail)} data-testid="invoice-detail-record-payment">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Record payment
                    </Button>
                  )}
                  {detail.status !== "cancelled" && detail.status !== "paid" && (
                    <Button
                      variant="ghost"
                      onClick={() => cancelInvoice(detail)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid="invoice-detail-cancel"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Cancel invoice
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ---------------- Payment dialog ---------------- */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Record payment {detail ? `for ${detail.number}` : ""}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPayment} className="space-y-4">
            <div>
              <Label htmlFor="amount">Amount *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                required
                value={payment.amount}
                onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
                data-testid="payment-amount"
              />
              {detail && (
                <p className="text-xs text-muted-foreground mt-1">
                  Outstanding balance: {fmtMoney(detail.balance, detail.currency)}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="paid_on">Date paid *</Label>
              <Input
                id="paid_on"
                type="date"
                required
                value={payment.paid_on}
                onChange={(e) => setPayment({ ...payment, paid_on: e.target.value })}
                data-testid="payment-date"
              />
            </div>
            <div>
              <Label htmlFor="method">Method</Label>
              <Select value={payment.method} onValueChange={(v) => setPayment({ ...payment, method: v })}>
                <SelectTrigger id="method" data-testid="payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="reference">Reference</Label>
              <Input
                id="reference"
                value={payment.reference}
                onChange={(e) => setPayment({ ...payment, reference: e.target.value })}
                placeholder="Transaction ID, cheque number..."
                data-testid="payment-reference"
              />
            </div>
            <div>
              <Label htmlFor="payment_notes">Notes</Label>
              <Textarea
                id="payment_notes"
                rows={2}
                value={payment.notes}
                onChange={(e) => setPayment({ ...payment, notes: e.target.value })}
                data-testid="payment-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPaymentOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={recording} data-testid="payment-save-btn">
                {recording ? "Saving..." : "Record payment"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KpiCard = ({ label, value, icon, testid }) => (
  <Card className="glass-card" data-testid={testid}>
    <CardContent className="p-4 space-y-1">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        <span className="uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="text-2xl font-heading font-bold">{value}</p>
    </CardContent>
  </Card>
);

// ===================== Tax / VAT summary card =====================
const TaxSummaryCard = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Handy "this tax year" / "last tax year" presets for UK treasurers (Apr 6 - Apr 5)
  const setTaxYear = (yearsAgo = 0) => {
    const now = new Date();
    const baseYear = now.getFullYear() - yearsAgo;
    // If today is before Apr 6, the current tax year started Apr 6 of baseYear-1
    const beforeApril6 = now.getMonth() < 3 || (now.getMonth() === 3 && now.getDate() < 6);
    const startYear = beforeApril6 ? baseYear - 1 : baseYear;
    setFromDate(`${startYear}-04-06`);
    setToDate(`${startYear + 1}-04-05`);
  };

  const runReport = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fromDate) qs.set("from_date", fromDate);
      if (toDate) qs.set("to_date", toDate);
      const r = await apiClient.get(`/invoices/reports/tax-summary?${qs.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tax summary");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    const token = localStorage.getItem("token");
    const qs = new URLSearchParams();
    if (fromDate) qs.set("from_date", fromDate);
    if (toDate) qs.set("to_date", toDate);
    const url = `${API}/invoices/reports/tax-summary.csv?${qs.toString()}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `tax-summary_${fromDate || "start"}_to_${toDate || "now"}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Failed to download CSV");
    }
  };

  return (
    <Card className="glass-card" data-testid="tax-summary-card">
      <CardHeader className="border-b border-white/5">
        <CardTitle className="font-heading flex items-center gap-2">
          <Calculator className="w-5 h-5 text-violet-400" />
          VAT &amp; tax summary
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <p className="text-sm text-muted-foreground">
          Accountant-friendly aggregate of invoices by <strong>issue date</strong>. Totals
          exclude cancelled invoices. Per-VAT-rate breakdown lets you reconcile VAT returns.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="tax_from" className="text-xs">From</Label>
            <Input
              id="tax_from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-[160px]"
              data-testid="tax-from-date"
            />
          </div>
          <div>
            <Label htmlFor="tax_to" className="text-xs">To</Label>
            <Input
              id="tax_to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-[160px]"
              data-testid="tax-to-date"
            />
          </div>
          <div className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setTaxYear(0)}
              data-testid="tax-preset-current"
            >
              <CalendarRange className="w-3.5 h-3.5 mr-1" />
              This tax year
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setTaxYear(1)}
              data-testid="tax-preset-last"
            >
              Last tax year
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setFromDate(""); setToDate(""); }}
              data-testid="tax-preset-all"
            >
              All time
            </Button>
          </div>
          <div className="flex-1" />
          <Button onClick={runReport} disabled={loading} data-testid="tax-run-btn">
            {loading ? "Running..." : "Run report"}
          </Button>
          <Button variant="outline" onClick={downloadCsv} data-testid="tax-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
        </div>

        {data && (
          <div className="space-y-4" data-testid="tax-summary-results">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <KpiCard label="Net subtotal" value={fmtMoney(data.totals.subtotal)} icon={<FileText className="w-4 h-4 text-blue-400" />} testid="tax-kpi-subtotal" />
              <KpiCard label="VAT collected" value={fmtMoney(data.totals.vat)} icon={<Calculator className="w-4 h-4 text-violet-400" />} testid="tax-kpi-vat" />
              <KpiCard label="Gross total" value={fmtMoney(data.totals.total)} icon={<Wallet className="w-4 h-4 text-amber-400" />} testid="tax-kpi-total" />
              <KpiCard label="Paid" value={fmtMoney(data.totals.amount_paid)} icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />} testid="tax-kpi-paid" />
              <KpiCard label="Outstanding" value={fmtMoney(data.totals.balance)} icon={<AlertCircle className="w-4 h-4 text-red-400" />} testid="tax-kpi-balance" />
            </div>

            {data.by_vat_rate?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Per VAT rate</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rate</TableHead>
                      <TableHead className="text-right">Net subtotal</TableHead>
                      <TableHead className="text-right">VAT</TableHead>
                      <TableHead className="text-right">Lines</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.by_vat_rate.map((r) => (
                      <TableRow key={r.rate} data-testid={`tax-rate-${r.rate}`}>
                        <TableCell className="font-mono">{r.rate}%</TableCell>
                        <TableCell className="text-right">{fmtMoney(r.subtotal)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(r.vat)}</TableCell>
                        <TableCell className="text-right">{r.line_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {data.by_status && Object.keys(data.by_status).length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">By status</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                      <TableHead className="text-right">VAT</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(data.by_status).map(([status, s]) => (
                      <TableRow key={status} data-testid={`tax-status-${status}`}>
                        <TableCell className="capitalize">{status.replace(/_/g, " ")}</TableCell>
                        <TableCell className="text-right">{s.count}</TableCell>
                        <TableCell className="text-right">{fmtMoney(s.subtotal)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(s.vat)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(s.total)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(s.amount_paid)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(s.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <p className="text-xs text-muted-foreground pt-2">
              {data.totals.invoice_count} invoice{data.totals.invoice_count !== 1 ? "s" : ""} counted in totals
              {data.period.from || data.period.to
                ? ` (issued ${data.period.from ? fmtDateUK(data.period.from) : "any date"} – ${data.period.to ? fmtDateUK(data.period.to) : "today"})`
                : " (all time)"}
              . Cancelled invoices are listed in the by-status breakdown but excluded from totals.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Invoices;
